import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Logger } from 'utils/Logger';
import { reelMakerApi } from '../ReelMaker.api';
import { Reel, ReelVersionSnapshot, ReelVersionsResponse } from '../ReelMaker.types';
import { ReelPlayer } from '../ReelPlayer';

const LOG_PREFIX = 'components/ReelMaker/dialogs/ReelPreviewDialog.tsx';

export interface ReelPreviewDialogProps {
    open: boolean;
    reel: Reel | null;
    onClose: () => void;
}

type VersionKey = 'draft' | number;

/**
 * Preview popup: plays the reel read-only. Defaults to the latest draft (when one exists) or the
 * latest published version; the dropdown lists every published `vN` snapshot for comparison.
 */
export const ReelPreviewDialog: React.FC<ReelPreviewDialogProps> = ({ open, reel, onClose }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [versions, setVersions] = useState<ReelVersionsResponse | null>(null);
    const [selected, setSelected] = useState<VersionKey | null>(null);
    const [snapshot, setSnapshot] = useState<ReelVersionSnapshot | null>(null);
    const [loadingList, setLoadingList] = useState(false);
    const [loadingSnapshot, setLoadingSnapshot] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reelId = reel?._id ?? null;

    // Load the version list whenever the dialog opens for a reel.
    useEffect(() => {
        if (!open || !reelId) return;
        let cancelled = false;
        setLoadingList(true);
        setError(null);
        setSnapshot(null);
        setVersions(null);
        reelMakerApi
            .listReelVersions(reelId)
            .then((data) => {
                if (cancelled) return;
                setVersions(data);
                // Default: latest draft if any, else latest published version.
                if (data.draft) setSelected('draft');
                else if (data.versions.length > 0) setSelected(data.versions[0].version);
                else setSelected('draft');
            })
            .catch((err) => {
                if (cancelled) return;
                const message = err instanceof Error ? err.message : String(err);
                Logger.error(LOG_PREFIX, `listReelVersions failed: ${message}`);
                setError(message);
            })
            .finally(() => {
                if (!cancelled) setLoadingList(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, reelId]);

    // Load the selected snapshot.
    const loadSnapshot = useCallback(async () => {
        if (!open || !reelId || selected == null) return;
        setLoadingSnapshot(true);
        setError(null);
        try {
            const data = await reelMakerApi.getReelVersion(reelId, selected === 'draft' ? 'draft' : selected);
            setSnapshot(data);
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            Logger.error(LOG_PREFIX, `getReelVersion failed: ${message}`);
            setError(message);
        } finally {
            setLoadingSnapshot(false);
        }
    }, [open, reelId, selected]);

    useEffect(() => {
        void loadSnapshot();
    }, [loadSnapshot]);

    const options = useMemo(() => {
        const out: Array<{ key: VersionKey; label: string; hint: string }> = [];
        if (versions?.draft) {
            out.push({ key: 'draft', label: `${versions.draft.label} (latest draft)`, hint: `Working copy · updated ${new Date(versions.draft.updatedAt).toLocaleString()}` });
        } else if (versions && versions.versions.length === 0) {
            out.push({ key: 'draft', label: 'v1-draft (latest draft)', hint: 'Working copy — nothing published yet' });
        }
        for (const v of versions?.versions ?? []) {
            out.push({
                key: v.version,
                label: v.version === versions?.publishedVersion ? `${v.label} (latest published)` : v.label,
                hint: `Published ${new Date(v.publishedAt).toLocaleString()} · ${v.width} × ${v.height}`,
            });
        }
        return out;
    }, [versions]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={fullScreen}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 1, flexWrap: 'wrap' }}>
                <PlayCircleOutlineIcon color="secondary" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 700 }} noWrap>
                        {reel?.name ?? 'Preview'}
                    </Typography>
                    {snapshot && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {snapshot.width} × {snapshot.height} px
                            {snapshot.isDraft ? ' · working copy (not published)' : snapshot.publishedAt ? ` · published ${new Date(snapshot.publishedAt).toLocaleString()}` : ''}
                        </Typography>
                    )}
                </Box>
                <FormControl size="small" sx={{ minWidth: 240 }} disabled={loadingList || options.length === 0}>
                    <InputLabel>Version</InputLabel>
                    <Select
                        label="Version"
                        value={selected == null ? '' : String(selected)}
                        onChange={(e) => {
                            const raw = String(e.target.value);
                            setSelected(raw === 'draft' ? 'draft' : Number(raw));
                        }}
                        renderValue={(value) => {
                            const opt = options.find((o) => String(o.key) === String(value));
                            return opt?.label ?? String(value);
                        }}
                    >
                        {options.map((o) => (
                            <MenuItem key={String(o.key)} value={String(o.key)}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {o.label}
                                        </Typography>
                                        <Chip size="small" label={o.key === 'draft' ? 'Draft' : 'Published'} color={o.key === 'draft' ? 'warning' : 'success'} variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {o.hint}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <IconButton size="small" aria-label="Close preview" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {error && <Alert severity="error">{error}</Alert>}
                {(loadingList || loadingSnapshot) && !snapshot && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
                        <CircularProgress color="secondary" />
                    </Box>
                )}
                {snapshot && (
                    <ReelPlayer
                        key={`${snapshot.reelId}:${snapshot.isDraft ? 'draft' : snapshot.version}:${snapshot.updatedAt ?? snapshot.publishedAt ?? ''}`}
                        width={snapshot.width}
                        height={snapshot.height}
                        canvas={snapshot.canvas}
                        autoPlay
                        loop
                        stageHeight={fullScreen ? 'calc(100vh - 220px)' : '62vh'}
                    />
                )}
                {snapshot && !snapshot.canvas && (
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                        This version has an empty canvas.
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ReelPreviewDialog;
